import { memo, useMemo } from 'react';

import type { GridColDef } from '@mui/x-data-grid-premium';

import type { InsertPropertyImagesBatchMutation } from '@graphql/__generated__/graphql';
import { EditableImages } from '@modules/_shared/components/image-gallery/EditableImages';
import { ListingPropertyImagesProvider } from '@modules/listings/ListingPropertyImages/contexts/ListingPropertyImagesProvider';
import { PropertyImageForm } from '@modules/listings/ListingPropertyImages/PropertyImageForm';
import type { PropertyImage } from '@modules/listings/ListingPropertyImages/types';
import { useLocale } from '@shared/hooks/locale';

type ListingPropertyImagesProps = {
  propertyId?: string | null;
  readOnly?: boolean;
  lotId?: string | null;
  canEditImagesMeta?: boolean;
  canOpenRightPanel?: boolean;
  onUpdate?: () => void;
  onAdd?: (
    newImages: NonNullable<
      InsertPropertyImagesBatchMutation['insert_property_images']
    >['returning'],
  ) => void;
  isOnePanelDisplay?: boolean;
  autoSave?: boolean;
  enableViewMode?: boolean;
  disableNavigation?: boolean;
  disableUpload?: boolean;
  showCaptions?: boolean;
  onShowCaptionsChange?: (show: boolean) => void;
  leftColumnHeader?: React.ReactNode;
};

export const ListingPropertyImages = memo(
  ({
    propertyId,
    readOnly = false,
    lotId,
    canEditImagesMeta = true,
    canOpenRightPanel = true,
    onUpdate,
    onAdd,
    isOnePanelDisplay = false,
    autoSave = false,
    enableViewMode = true,
    disableNavigation = false,
    disableUpload = false,
    showCaptions,
    onShowCaptionsChange,
    leftColumnHeader,
  }: ListingPropertyImagesProps) => {
    const { t } = useLocale();
    const shouldSelectFirst =
      canEditImagesMeta && canOpenRightPanel && !isOnePanelDisplay;

    const metaColumns: GridColDef<PropertyImage>[] = useMemo(
      () => [
        { field: 'display_on_listing', headerName: t('Listing') },
        { field: 'display_on_brochure', headerName: t('Brochure') },
      ],
      [t],
    );

    if (propertyId == null) {
      return null;
    }

    return (
      <ListingPropertyImagesProvider
        propertyId={propertyId}
        lotId={lotId}
        metaColumns={metaColumns}
        canEditMeta={!readOnly && canEditImagesMeta}
        autoSave={autoSave}
        enableViewMode={enableViewMode}
        autoSelectFirst={shouldSelectFirst}
        onUpdate={onUpdate}
        onAdd={onAdd}
        readOnly={readOnly}
        initialViewMode={readOnly ? 'grid' : 'list'}
      >
        <EditableImages
          isOnePanelDisplay={isOnePanelDisplay}
          canOpenRightPanel={!readOnly && canOpenRightPanel}
          disableNavigation={disableNavigation}
          disableUpload={disableUpload}
          showCaptions={showCaptions}
          onShowCaptionsChange={onShowCaptionsChange}
          leftColumnHeader={leftColumnHeader}
          ImageForm={PropertyImageForm}
          lotId={lotId}
        />
      </ListingPropertyImagesProvider>
    );
  },
);
